import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import { alpacaService } from '../../services/alpaca';
import { exchangeConnectorService } from '../../services/exchangeConnector';
import { AlpacaConfig as AlpacaConfigType } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { doc, setDoc, getDoc, collection, getDocs, query, where, deleteField, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';

interface Props {
  onConfigSaved: () => void;
}

export const AlpacaConfig: React.FC<Props> = ({ onConfigSaved }) => {
  const { state } = useAuth();
  const [config, setConfig] = useState<AlpacaConfigType>({
    apiKey: '',
    secretKey: '',
    paper: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [migrated, setMigrated] = useState(false);

  useEffect(() => {
    const loadConfig = async () => {
      if (!state.user) return;
      
      try {
        // First check for credentials in the new api_credentials subcollection
        const apiCredentialsRef = collection(db, `users/${state.user.id}/api_credentials`);
        const alpacaQuery = query(apiCredentialsRef, where("provider", "==", "alpaca"));
        const querySnapshot = await getDocs(alpacaQuery);
        
        if (!querySnapshot.empty) {
          const credentialDoc = querySnapshot.docs[0];
          const credentialData = credentialDoc.data();
          
          if (credentialData) {
            setConfig({
              apiKey: credentialData.apiKey || '',
              secretKey: credentialData.secretKey || '',
              paper: credentialData.paper !== undefined ? credentialData.paper : true,
            });
            alpacaService.initialize({
              apiKey: credentialData.apiKey,
              secretKey: credentialData.secretKey,
              paper: credentialData.paper !== undefined ? credentialData.paper : true,
            });
            console.log('Loaded Alpaca API keys from api_credentials subcollection');
            return;
          }
        }
        
        // Fallback: Try to load from user preferences
        const userRef = doc(db, 'users', state.user.id);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const alpacaConfig = userData.preferences?.apiKeys?.alpaca;
          
          if (alpacaConfig && alpacaConfig.apiKey && alpacaConfig.secretKey) {
            setConfig({
              apiKey: alpacaConfig.apiKey || '',
              secretKey: alpacaConfig.secretKey || '',
              paper: alpacaConfig.paper !== undefined ? alpacaConfig.paper : true,
            });
            alpacaService.initialize(alpacaConfig);
            setMigrated(true);
            console.log('Found Alpaca API keys in user preferences (legacy location)');
            
            // Automatically migrate to new location
            await setDoc(doc(collection(db, `users/${state.user.id}/api_credentials`)), {
              provider: "alpaca",
              apiKey: alpacaConfig.apiKey,
              secretKey: alpacaConfig.secretKey,
              paper: alpacaConfig.paper !== undefined ? alpacaConfig.paper : true,
              userId: state.user.id,
              email: state.user.email,
              updatedAt: new Date().toISOString(),
              migratedFrom: "user_preferences"
            });
            
            console.log('Migrated Alpaca API keys from user preferences to api_credentials subcollection');
            
            // Remove from user preferences
            try {
              await updateDoc(userRef, {
                "preferences.apiKeys.alpaca": deleteField()
              });
              console.log('Removed Alpaca API keys from user preferences after migration');
            } catch (err) {
              console.error('Error removing Alpaca API keys from user preferences:', err);
            }
          }
        }
        
        // Try to load from localStorage as a last resort for backward compatibility
        const savedConfig = localStorage.getItem('alpacaConfig');
        if (savedConfig) {
          const parsedConfig = JSON.parse(savedConfig);
          setConfig(parsedConfig);
          alpacaService.initialize(parsedConfig);
        }
      } catch (err) {
        console.error('Error loading Alpaca config:', err);
      }
    };
    
    loadConfig();
  }, [state.user]);

  const validateConfig = () => {
    if (!config.apiKey.trim()) {
      setError('API Key is required');
      return false;
    }
    if (!config.secretKey.trim()) {
      setError('Secret Key is required');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    try {
      setError(null);
      if (!validateConfig()) return;

      setLoading(true);
      alpacaService.initialize(config);

      // Test the connection by getting account info
      await alpacaService.getAccount();

      // Save to api_credentials subcollection
      if (state.user) {
        const apiCredentialsRef = collection(db, `users/${state.user.id}/api_credentials`);
        const alpacaQuery = query(apiCredentialsRef, where("provider", "==", "alpaca"));
        const querySnapshot = await getDocs(alpacaQuery);
        
        if (!querySnapshot.empty) {
          const credentialDoc = querySnapshot.docs[0];
          await updateDoc(credentialDoc.ref, {
            apiKey: config.apiKey,
            secretKey: config.secretKey,
            paper: config.paper,
            updatedAt: new Date().toISOString(),
          });
        } else {
          await setDoc(doc(apiCredentialsRef), {
            provider: "alpaca",
            apiKey: config.apiKey,
            secretKey: config.secretKey,
            paper: config.paper,
            userId: state.user.id,
            email: state.user.email,
            updatedAt: new Date().toISOString(),
          });
        }
      }
      
      // Save to localStorage for backward compatibility
      localStorage.setItem('alpacaConfig', JSON.stringify(config));
      
      // If user is logged in, remove from user preferences if migrated
      if (state.user && migrated) {
        try {
          const userRef = doc(db, 'users', state.user.id);
          await updateDoc(userRef, {
            "preferences.apiKeys.alpaca": deleteField()
          });
          console.log('Removed Alpaca API keys from user preferences after migration');
        } catch (err) {
          console.error('Error removing Alpaca API keys from user preferences:', err);
        }
      }
      
      // Automatically establish connection using the exchange connector service
      if (state.user) {
        console.log('Initializing Alpaca connection after configuration...');
        const connectionResult = await exchangeConnectorService.initializeSingleConnection('alpaca', state.user.id);
        
        if (connectionResult) {
          console.log('Alpaca connection established automatically after configuration');
        } else {
          console.warn('Failed to establish automatic Alpaca connection after configuration');
        }
      }
      
      setSuccess(true);
      onConfigSaved();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to connect to Alpaca');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="h6" gutterBottom>
            Alpaca API Configuration
          </Typography>

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <TextField
            label="API Key"
            value={config.apiKey}
            onChange={(e) => setConfig({ ...config, apiKey: e.target.value })}
            required
            fullWidth
            type="password"
            error={Boolean(error && !config.apiKey)}
          />

          <TextField
            label="Secret Key"
            value={config.secretKey}
            onChange={(e) => setConfig({ ...config, secretKey: e.target.value })}
            required
            fullWidth
            type="password"
            error={Boolean(error && !config.secretKey)}
          />

          <FormControlLabel
            control={
              <Switch
                checked={config.paper}
                onChange={(e) => setConfig({ ...config, paper: e.target.checked })}
              />
            }
            label="Paper Trading"
          />

          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} color="inherit" />}
          >
            {loading ? 'Testing Connection...' : 'Save Configuration'}
          </Button>
        </Box>
      </CardContent>

      <Snackbar
        open={success}
        autoHideDuration={6000}
        onClose={() => setSuccess(false)}
        message="Configuration saved successfully"
      />
    </Card>
  );
};
